import type { RentabilidadDailyRow } from '../domain/reportsIntelligenceTypes'
import type { SalesDailyRow } from '../domain/reportsTypes';
import { startOfWeekMondayLocal } from './reportsScreen.utils';

export type ChartBucketMode = 'day' | 'week' | 'month';

/**
 * Criterio por span del rango (no por cantidad de filas con datos):
 * hasta ~2 semanas día a día; hasta ~16 semanas por semana; rangos largos por mes.
 * Meta: ≤ ~16 barras legibles sin scroll para los presets habituales (7d → 7 barras, 30d → ~5, 12m → 12).
 */
export function chooseChartBucketMode(rangeDays: number): ChartBucketMode {
  if (rangeDays <= 16) return 'day';
  if (rangeDays <= 112) return 'week';
  return 'month';
}

function parseChartDay(day: string): Date {
  const ymd = day.length >= 10 ? day.slice(0, 10) : day;
  const d = new Date(`${ymd}T12:00:00`);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function weekBucketKeyFromDay(day: string): string {
  return ymdLocal(startOfWeekMondayLocal(parseChartDay(day)));
}

function monthBucketKeyFromDay(day: string): string {
  const d = parseChartDay(day);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Inicio de semana (lunes) en dd/mm — compacto para etiquetas de barras. */
function formatWeekBucketLabelShort(mondayYmd: string): string {
  const d = parseChartDay(mondayYmd);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

function formatMonthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map((x) => parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return monthKey;
  const d = new Date(y, m - 1, 1, 12, 0, 0, 0);
  return d.toLocaleDateString('es-AR', { month: 'short', year: 'numeric' });
}

export type AggregatedSalesBar = {
  key: string;
  label: string;
  salesTotal: number;
  salesCount: number;
};

export function aggregateSalesDailyForChart(rows: SalesDailyRow[], mode: ChartBucketMode): AggregatedSalesBar[] {
  if (rows.length === 0) return [];
  if (mode === 'day') {
    return rows.map((r) => ({
      key: r.day,
      label:
        r.day.length >= 10
          ? parseChartDay(r.day).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
          : r.day,
      salesTotal: r.salesTotal,
      salesCount: r.salesCount,
    }));
  }
  const map = new Map<string, { salesTotal: number; salesCount: number; sortKey: string }>();
  for (const r of rows) {
    const bucket =
      mode === 'week' ? weekBucketKeyFromDay(r.day) : monthBucketKeyFromDay(r.day);
    const prev = map.get(bucket);
    if (prev) {
      prev.salesTotal += r.salesTotal;
      prev.salesCount += r.salesCount;
    } else {
      map.set(bucket, { salesTotal: r.salesTotal, salesCount: r.salesCount, sortKey: bucket });
    }
  }
  const sorted = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  return sorted.map(([bucket, v]) => ({
    key: bucket,
    label: mode === 'week' ? formatWeekBucketLabelShort(bucket) : formatMonthLabel(bucket),
    salesTotal: v.salesTotal,
    salesCount: v.salesCount,
  }));
}

export type AggregatedMarginBar = {
  key: string;
  label: string;
  margenReposicionTotal: number;
  salesTotal: number;
  costoReposicionTotal: number;
};

export function aggregateRentabilidadDailyForChart(
  rows: RentabilidadDailyRow[],
  mode: ChartBucketMode
): AggregatedMarginBar[] {
  if (rows.length === 0) return [];
  if (mode === 'day') {
    return rows.map((r) => ({
      key: r.day,
      label:
        r.day.length >= 10
          ? parseChartDay(r.day).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
          : r.day,
      margenReposicionTotal: r.margenReposicionTotal,
      salesTotal: r.salesTotal,
      costoReposicionTotal: r.costoReposicionTotal,
    }));
  }
  const map = new Map<string, { margen: number; sales: number; costo: number }>();
  for (const r of rows) {
    const bucket =
      mode === 'week' ? weekBucketKeyFromDay(r.day) : monthBucketKeyFromDay(r.day);
    const prev = map.get(bucket);
    if (prev) {
      prev.margen += r.margenReposicionTotal;
      prev.sales += r.salesTotal;
      prev.costo += r.costoReposicionTotal;
    } else {
      map.set(bucket, {
        margen: r.margenReposicionTotal,
        sales: r.salesTotal,
        costo: r.costoReposicionTotal,
      });
    }
  }
  const sorted = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  return sorted.map(([bucket, v]) => ({
    key: bucket,
    label: mode === 'week' ? formatWeekBucketLabelShort(bucket) : formatMonthLabel(bucket),
    margenReposicionTotal: v.margen,
    salesTotal: v.sales,
    costoReposicionTotal: v.costo,
  }));
}

export function chartBucketHint(mode: ChartBucketMode): string | null {
  if (mode === 'day') return null;
  if (mode === 'week') return 'Por semana (lunes a domingo). Cada barra suma esa semana.';
  return 'Por mes. Cada barra suma el mes.';
}
